import store from '@/store';
import {
  getModule,
  Module,
  VuexModule,
  Mutation,
  Action
} from 'vuex-module-decorators';
import { retrieveCatalogSessionFilters, retrieveCatalogSessionList } from '@/services/catalog/api';
import MutationNames from './mutationNames';
import { CatalogSession, CatalogSessionList, CatalogSessionListRequest, FilterListChangeData, FilterListItem, SessionFilterParameters } from '@/Model/sessions/catalogTypes';
import { ApiErrorResponse } from '@/Model/error/api/types';
import APP_UTILITIES from '@/utilities/commonFunctions';

const {
  FETCH_SESSION_FILTERS_FAILED,
  FETCH_SESSION_FILTERS_REQUESTED,
  FETCH_SESSION_FILTERS_SUCCEEDED,
  FETCH_SESSIONS_FAILED,
  FETCH_SESSIONS_REQUESTED,
  FETCH_SESSIONS_SUCCEEDED,
  FORMAT_CATALOG_LIST_REQUEST,
  RESET_CATALOG_STORE,
  RESET_FILTER_DATA,
  SET_CURRENT_LOCATION_IDS_FILTERED,
  SET_CURRENT_PROGRAM_IDS_FILTERED,
  SET_CURRENT_SESSION_DATE_END_FILTER,
  SET_CURRENT_SESSION_DATE_START_FILTER,
  UPDATE_PAGE_NUMBER,
} = MutationNames;

@Module({
  namespaced: true,
  name: 'catalogStore',
  store,
  dynamic: true
})
export class CatalogStoreModule extends VuexModule {
  public static readonly maxResultsPerPage: number = 15;
  public static readonly initialFilterDisplayLength: number = 5;
  currentCatalogSessionList: Array<CatalogSession> = [];
  unfilteredCatalogSessionData: Array<SessionFilterParameters> = [];
  isInitialLoadingSessions: boolean = true;
  isLoadingSessions: boolean = false;
  didSessionFetchFail: boolean = false;
  sessionsFetchError: ApiErrorResponse | null = null;
  catalogListRequest: CatalogSessionListRequest = {
    accountId: 0,
    sessionIdList: [],
    page: 0,
    count: 0
  };

  //Filter parameters
  isLoadingFilters: boolean = true;
  filterFetchError: ApiErrorResponse | null = null;
  isFilterDisabled: boolean = false;
  currentProgramIdsFiltered: Array<number> = [];
  currentLocationIdsFiltered: Array<number> = [];
  currentSessionStartDateFilter: Date | null = null;
  currentSessionEndDateFilter: Date | null = null;

  currentPage: number = 0;
  totalSetCount: number = 0;

  // An ID which is changed each time a new set of filters is selected.
  loadingId: number = 0;

  get shouldLoadMoreSessions(): boolean {
    return this.isInitialLoadingSessions || (this.currentCatalogSessionList.length < this.totalSetCount);
  }

  get programFilterOptions(): FilterListItem[] {
    return this.unfilteredCatalogSessionData.reduce((acc: Array<FilterListItem>, curr) => {
      const foundValue = acc.find(x => x.id === curr.programId);
      const shouldAddSessionIdBasedOnOtherFilter = this.currentLocationIdsFiltered.length > 0
        ? this.currentLocationIdsFiltered.some(x => x === curr.siteLocationId)
        : true;
      const shouldAddBasedOnStartDate = this.currentSessionStartDateFilter
        ? curr.startDate.toISOString().split('T')[0] >= this.currentSessionStartDateFilter.toISOString().split('T')[0]
        : true;
      const shouldAddBasedOnEndDate = this.currentSessionEndDateFilter
        ? curr.endDate.toISOString().split('T')[0] <= this.currentSessionEndDateFilter.toISOString().split('T')[0]
        : true;
      if (foundValue) {
        if (shouldAddSessionIdBasedOnOtherFilter && shouldAddBasedOnStartDate && shouldAddBasedOnEndDate) {
          foundValue.sessionIds.push(curr.sessionId);
        }
      }
      else {
        const newObject = {
          id: curr.programId,
          name: curr.programName,
          sessionIds: []
        } as FilterListItem;

        if (shouldAddSessionIdBasedOnOtherFilter && shouldAddBasedOnStartDate && shouldAddBasedOnEndDate) {
          newObject.sessionIds.push(curr.sessionId);
        }

        acc.push(newObject);
      }
      return acc;
    }, []);
  }

  get locationFilterOptions(): FilterListItem[] {
    return this.unfilteredCatalogSessionData.filter(item => item.siteLocationId).reduce((acc: Array<FilterListItem>, curr) => {
      const foundValue = acc.find(x => x.id === curr.siteLocationId);
      const shouldAddSessionIdBasedOnOtherFilter = this.currentProgramIdsFiltered.length > 0
        ? this.currentProgramIdsFiltered.some(x => x === curr.programId)
        : true;
      const shouldAddBasedOnStartDate = this.currentSessionStartDateFilter
        ? curr.startDate >= this.currentSessionStartDateFilter
        : true;
      const shouldAddBasedOnEndDate = this.currentSessionEndDateFilter
        ? curr.endDate <= this.currentSessionEndDateFilter
        : true;
      if (foundValue) {
        if (shouldAddSessionIdBasedOnOtherFilter && shouldAddBasedOnStartDate && shouldAddBasedOnEndDate) {
          foundValue.sessionIds.push(curr.sessionId);
        }
      }
      else {
        const newObject = {
          id: curr.siteLocationId,
          name: curr.siteLocationName,
          sessionIds: []
        } as FilterListItem;

        if (shouldAddSessionIdBasedOnOtherFilter && shouldAddBasedOnStartDate && shouldAddBasedOnEndDate) {
          newObject.sessionIds.push(curr.sessionId);
        }

        acc.push(newObject);
      }
      return acc;
    }, []).sort((a, b) => a.name.localeCompare(b.name));
  }


  @Mutation
  [FETCH_SESSIONS_REQUESTED]() {
    this.isLoadingSessions = true;
    this.didSessionFetchFail = false;
    this.sessionsFetchError = null;
  }

  @Mutation
  [FETCH_SESSIONS_FAILED](error: ApiErrorResponse) {
    this.isInitialLoadingSessions = false;
    this.isLoadingSessions = false;
    this.didSessionFetchFail = true;
    this.sessionsFetchError = error;
  }

  @Mutation
  [FETCH_SESSIONS_SUCCEEDED](catalogSessionList: CatalogSessionList) {
    this.isInitialLoadingSessions = false;
    this.isLoadingSessions = false;
    this.totalSetCount = catalogSessionList.totalSetCount;
    this.currentCatalogSessionList.push(...catalogSessionList.results);
    this.didSessionFetchFail = false;
    this.sessionsFetchError = null;
    this.isFilterDisabled = false;
  }

  @Mutation
  [RESET_CATALOG_STORE]() {
    this.currentCatalogSessionList = [];
    this.unfilteredCatalogSessionData = [];
    this.isInitialLoadingSessions = true;
    this.isLoadingSessions = false;
    this.didSessionFetchFail = false;
    this.currentProgramIdsFiltered = [];
    this.currentLocationIdsFiltered = [];
    this.sessionsFetchError = null;
    this.currentSessionStartDateFilter = null;
    this.currentSessionEndDateFilter = null;
    this.catalogListRequest = {
      accountId: 0,
      sessionIdList: [],
      page: 0,
      count: 0
    };
    this.isLoadingFilters = true;
    this.filterFetchError = null;
    this.isFilterDisabled = false;

    this.currentPage = 0;
    this.totalSetCount = 0;
    this.loadingId = 0;
  }

  @Mutation
  [UPDATE_PAGE_NUMBER]() {
    this.currentPage++;
  }

  @Mutation
  [FETCH_SESSION_FILTERS_REQUESTED]() {
    this.isLoadingFilters = true;
  }

  @Mutation
  [FETCH_SESSION_FILTERS_SUCCEEDED](catalogSessions: Array<SessionFilterParameters>) {
    this.unfilteredCatalogSessionData = catalogSessions;
    for (const session of this.unfilteredCatalogSessionData) {
      if (session.startDate) {
        session.startDate = new Date(session.startDate);
      }
      if (session.endDate) {
        session.endDate = new Date(session.endDate);
      }
    }
    this.isLoadingFilters = false;
  }

  @Mutation
  [FETCH_SESSION_FILTERS_FAILED](error: ApiErrorResponse) {
    this.isLoadingFilters = false;
    this.filterFetchError = error;
  }

  @Mutation
  [FORMAT_CATALOG_LIST_REQUEST](accountId: number) {
    let filterData = this.unfilteredCatalogSessionData;

    if (this.currentSessionStartDateFilter) {
      filterData = filterData.filter(session => session.startDate.setHours(0, 0, 0) >= this.currentSessionStartDateFilter!.setHours(0, 0, 0));
    }
    if (this.currentSessionEndDateFilter) {
      filterData = filterData.filter(session => session.endDate.setHours(0, 0, 0) <= this.currentSessionEndDateFilter!.setHours(0, 0, 0));
    }

    const sessionIds = filterData.filter(session => this.currentProgramIdsFiltered.length < 1 ||
      this.currentProgramIdsFiltered.some(x => x === session.programId)).filter(session => this.currentLocationIdsFiltered.length < 1 ||
        this.currentLocationIdsFiltered.some(x => x === session.siteLocationId)).map(session => session.sessionId);


    this.catalogListRequest = {
      accountId: accountId,
      sessionIdList: sessionIds,
      count: CatalogStoreModule.maxResultsPerPage,
      page: this.currentPage
    } as CatalogSessionListRequest;
  }

  @Mutation
  [SET_CURRENT_PROGRAM_IDS_FILTERED](currentProgramIdsFiltered: Array<number>) {
    this.currentProgramIdsFiltered = currentProgramIdsFiltered;
  }

  @Mutation
  [SET_CURRENT_LOCATION_IDS_FILTERED](currentLocationIdsFiltered: Array<number>) {
    this.currentLocationIdsFiltered = currentLocationIdsFiltered;
  }

  @Mutation
  [SET_CURRENT_SESSION_DATE_END_FILTER](date: Date | null) {
    this.currentSessionEndDateFilter = date;
  }

  @Mutation
  [SET_CURRENT_SESSION_DATE_START_FILTER](date: Date | null) {
    this.currentSessionStartDateFilter = date;
  }

  @Mutation
  [RESET_FILTER_DATA]() {
    this.isFilterDisabled = true;
    this.currentCatalogSessionList = [];
    this.currentPage = 0;
    this.isInitialLoadingSessions = true;
    this.loadingId++;
  }

  @Action
  onLocationFilterListChange(changeData: FilterListChangeData) {
    this.RESET_FILTER_DATA();
    let currentLocationIdsFiltered = [...this.currentLocationIdsFiltered];
    if (changeData.isAdded) {
      currentLocationIdsFiltered.push(changeData.checkedItem.id);
    }
    else {
      currentLocationIdsFiltered = currentLocationIdsFiltered.filter(x => x !== changeData.checkedItem.id);
    }

    this.SET_CURRENT_LOCATION_IDS_FILTERED(currentLocationIdsFiltered);
  }


  @Action
  onProgramFilterListChange(changeData: FilterListChangeData) {
    this.RESET_FILTER_DATA();
    let currentProgramIdsFiltered = [...this.currentProgramIdsFiltered];
    if (changeData.isAdded) {
      currentProgramIdsFiltered.push(changeData.checkedItem.id);
    }
    else {
      currentProgramIdsFiltered = currentProgramIdsFiltered.filter(x => x !== changeData.checkedItem.id);
    }

    this.SET_CURRENT_PROGRAM_IDS_FILTERED(currentProgramIdsFiltered);
  }

  @Action
  async retrieveCatalogSessions(accountId: number) {
    if (this.shouldLoadMoreSessions) {
      try {
        this.UPDATE_PAGE_NUMBER();
        this.FETCH_SESSIONS_REQUESTED();

        this.FORMAT_CATALOG_LIST_REQUEST(accountId);

        const response = await retrieveCatalogSessionList(this.catalogListRequest);

        this.FETCH_SESSIONS_SUCCEEDED(response.data);
      }
      catch (error) {
        const apiErrorResponse: ApiErrorResponse = APP_UTILITIES.errorToApiErrorResponse(error);
        this.FETCH_SESSIONS_FAILED(apiErrorResponse);
      }
    }
  }

  @Action
  async retrieveSessionFilters(accountId: number) {
    try {
      this.FETCH_SESSION_FILTERS_REQUESTED();

      const response = await retrieveCatalogSessionFilters(accountId);
      if (response && response.data) {
        this.FETCH_SESSION_FILTERS_SUCCEEDED(response.data.sessions);
      }

    }
    catch (error) {
      const apiErrorResponse: ApiErrorResponse = APP_UTILITIES.errorToApiErrorResponse(error);
      this.FETCH_SESSION_FILTERS_FAILED(apiErrorResponse);

    }
  }
}


export default getModule(CatalogStoreModule);